import React, { useState } from "react"

import clsx from "clsx"
import Loading from "@/components/Loading"
import DataTableWithPagination from "@/components/DataTableWithPagination"
import useStyles from "@/@integrations/WABA/components/WabaAccountInfo/styles"
import WABAAccountInfoSkeleton from "@/@integrations/WABA/skeletons/WABAAccountInfoSkeleton"
import JsonEditor from "@/components/JsonEditor/index"
import WABAAccountInfoCardsSkeleton from "@/@integrations/WABA/skeletons/WabaAccountInfoCardsSkeleton"

import {
	Eye as ViewPayloadIcon,
	Activity as AccountStatusIcon,
	ShieldCheck as AccountQualityIcon,
	MessageSquare as AccountMessagingLimitIcon,
	AlertTriangle as AccountRestrictionIcon
} from "lucide-react"

import {
	Grid,
	Card,
	Typography,
	Paper,
	IconButton,
	Divider as MuiDivider
} from "@material-ui/core"
import { useGlobalStateStore } from "@/store/GlobalState"
import { WabaAppInfoResponse, WabaEventHistoryFormatted } from "@/@integrations/WABA/utils/wabaAccountInfo"
import { TableColumn } from "@/components/DataTable"
import { DefaultWhereDataType } from "@/utils/pagination"
import { InfoDialog } from "letalkui"
import { colors } from "@/styles/colors"
import { WABAAppData } from "@/@integrations/WABA/protocols/channel"

type WabaAccountInfoProps = {
	appData?: WABAAppData
	appInfo: WabaAppInfoResponse
	eventHistory: WabaEventHistoryFormatted[]
	isLoading?: boolean
	currentPage: number
	rowsPerPage: number
	totalEventRows: number
	hasActiveRestrictions: boolean
	onPaginationChange: (data: Partial<DefaultWhereDataType>) => void
}

const WabaAccountInfo: React.FC<WabaAccountInfoProps> = (props) => {
	const {
		appInfo,
		eventHistory,
		isLoading = false,
		currentPage,
		rowsPerPage,
		totalEventRows,
		hasActiveRestrictions,
		appData,
		onPaginationChange
	} = props

	const classes = useStyles()
	const globalStateStore = useGlobalStateStore()

	const isAdminUser = globalStateStore.user?.extra_data?.is_admin_mode

	const [isPayloadDialogOpen, setIsPayloadDialogOpen] = useState(false)
	const [selectedEventPayload, setSelectedEventPayload] = useState<Record<string, unknown>>({})

	const handleOpenEventPayloadDialog = (payload: Record<string, unknown>) => {
		setSelectedEventPayload(payload)
		setIsPayloadDialogOpen(true)
	}

	const handleCloseEventPayloadDialog = () => {
		setIsPayloadDialogOpen(false)
		setSelectedEventPayload({})
	}

	const eventTypeColumn: TableColumn<WabaEventHistoryFormatted["history"]> = {
		field: "eventTypeLabel",
		label: "Tipo de evento"
	}

	const eventSubTypeColumn: TableColumn<WabaEventHistoryFormatted["history"]> = {
		field: "eventSubTypeLabel",
		label: "Subtipo de evento"
	}

	const eventCreatedAtColumn: TableColumn<WabaEventHistoryFormatted["history"]> = {
		field: "createdAt",
		label: "Data/hora"
	}

	const adminPayloadActionColumn: TableColumn<WabaEventHistoryFormatted["history"]> = {
		field: "payload",
		label: "Ação",
		align: "center",
		rowRender: (payload) => (
			<Grid container justifyContent="center" alignItems="center" spacing={1}>
				<Grid item>
					<IconButton
						size="small"
						onClick={() => handleOpenEventPayloadDialog(payload as Record<string, unknown>)}
						color="primary"
					>
						<ViewPayloadIcon size={16} />
					</IconButton>
				</Grid>
				<Grid item>
					<Typography
						variant="caption"
						color="primary"
						className={classes.showMoreButton}
						onClick={() => handleOpenEventPayloadDialog(payload as Record<string, unknown>)}
					>
						Ver mais
					</Typography>
				</Grid>
			</Grid>
		)
	}

	const baseEventHistoryColumns: TableColumn<WabaEventHistoryFormatted["history"]>[] = [
		eventTypeColumn,
		eventSubTypeColumn,
		eventCreatedAtColumn
	]

	const adminEventHistoryColumns: TableColumn<WabaEventHistoryFormatted["history"]>[] = [
		...baseEventHistoryColumns,
		adminPayloadActionColumn
	]

	const visibleEventHistoryColumns = isAdminUser ? adminEventHistoryColumns : baseEventHistoryColumns

	const renderAccountRestrictionWarnings = () => {
		const eventsHistoryWithWarning = eventHistory.filter((event) => event.warnings?.hasRestriction)

		return (
			<Grid container spacing={2}>
				{eventsHistoryWithWarning.length > 0 && eventsHistoryWithWarning.map((event) =>
					event.warnings?.restrictionsInfo?.map((restriction, index) => (
						<Grid item xs={6} key={index}>
							<Card className={clsx(classes.restrictionInfoCard, classes.card)}>
								<Grid container justifyContent="space-between" alignItems="center">
									<Grid item>
										<Typography variant="h5" className={classes.cardTitle}>
											{restriction.type || "-"}
										</Typography>
									</Grid>
									<Grid item>
										<AccountRestrictionIcon size={24} color={colors.red["500"]} />
									</Grid>
								</Grid>
								<Typography variant="body1" className={classes.cardSubtitle}>
									Expiração: {restriction.expiration ?? new Date()}
								</Typography>
							</Card>
						</Grid>
					))
				)}
			</Grid>
		)
	}

	return (
		<Grid container className={classes.container} spacing={2}>
			<Loading
				loading={isLoading}
				customLoadingElement={<WABAAccountInfoSkeleton />}
			>
				<Grid item xs={12}>
					<Typography variant="h4" className={classes.tableTitle}>
						Informações sobre a conta
					</Typography>

					<Loading
						loading={isLoading}
						customLoadingElement={<WABAAccountInfoCardsSkeleton />}
					>
						<Grid container spacing={2}>
							{
								appInfo.accountStatusLabel && (
									<Grid item xs={4}>
										<Card className={clsx({
											[classes.accountBannedStatusCard]: appData?.status === "banned",
											[classes.accountStatusCard]: appData?.status !== "banned",
											[classes.card]: true
										})}>
											<Grid container justifyContent="space-between" alignItems="center">
												<Grid item>
													<Typography variant="h5" className={classes.cardTitle}>
														{appInfo.accountStatusLabel}
													</Typography>
												</Grid>
												<Grid item>
													<AccountStatusIcon size={24} color={clsx({
														[colors.red["400"]]: appData?.status === "banned",
														[colors.purple["400"]]: appData?.status !== "banned"
													})} />
												</Grid>
											</Grid>
											<Typography variant="body1" className={classes.cardSubtitle}>
												Status da conta
											</Typography>
										</Card>
									</Grid>
								)
							}

							{
								appInfo.phoneQualityLabel && (
									<Grid item xs={4}>
										<Card className={clsx(classes.phoneQualityCard, classes.card)}>
											<Grid container justifyContent="space-between" alignItems="center">
												<Grid item>
													<Typography variant="h5" className={classes.cardTitle}>
														{appInfo.phoneQualityLabel}
													</Typography>
												</Grid>
												<Grid item>
													<AccountQualityIcon size={24} color={colors.blue["700"]} />
												</Grid>
											</Grid>
											<Typography variant="body1" className={classes.cardSubtitle}>
												Qualidade da conta
											</Typography>
										</Card>
									</Grid>
								)
							}

							{
								appInfo.messagingLimitLabel && (
									<Grid item xs={4}>
										<Card className={clsx(classes.messagingLimitCard, classes.card)}>
											<Grid container justifyContent="space-between" alignItems="center">
												<Grid item>
													<Typography variant="h5" className={classes.cardTitle}>
														{appInfo.messagingLimitLabel}
													</Typography>
												</Grid>
												<Grid item>
													<AccountMessagingLimitIcon size={24} color={colors.green["700"]} />
												</Grid>
											</Grid>
											<Typography variant="body1" className={classes.cardSubtitle}>
												Limite de mensagens da conta
											</Typography>
										</Card>
									</Grid>
								)
							}
						</Grid>
					</Loading>
				</Grid>

				<Grid item xs={12}>
					<Grid container spacing={2}>
						<Grid item xs={12}>
							<MuiDivider orientation="horizontal" />
						</Grid>

						{hasActiveRestrictions && (
							<Grid item xs={12}>
								{renderAccountRestrictionWarnings()}
							</Grid>
						)}

						{eventHistory.length > 0 && (
							<Grid item xs={12}>
								<Grid container>
									<Grid item xs={12}>
										<Typography variant="h4" className={classes.tableTitle}>
											Histórico de Eventos
										</Typography>
									</Grid>

									<Grid item xs={12}>
										<Paper elevation={0}>
											<DataTableWithPagination
												columns={visibleEventHistoryColumns}
												data={eventHistory.map((event) => event.history)}
												page={currentPage}
												rowsPerPage={rowsPerPage}
												totalRows={totalEventRows}
												onPaginationChange={onPaginationChange}
											/>
										</Paper>
									</Grid>

									<Grid item xs={12}>
										<MuiDivider orientation="horizontal" />
									</Grid>
								</Grid>
							</Grid>
						)}
					</Grid>
				</Grid>
			</Loading>

			{isAdminUser && (
				<InfoDialog
					open={isPayloadDialogOpen}
					onClose={handleCloseEventPayloadDialog}
					title="Payload do evento"
				>
					<JsonEditor
						mode="json"
						width="30.25rem"
						height="18.75rem"
						defaultValue={JSON.stringify(selectedEventPayload, null, 2)}
					/>
				</InfoDialog>
			)}
		</Grid>
	)
}

export default WabaAccountInfo
